import React, { useMemo } from "react";

import { Box, Typography, Alert } from "@mui/material";
import dayjs from "dayjs";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  LabelList,
} from "recharts";

import { HolidayClubAttendanceNumberResponse } from "../../api/PartnerPortalGatewayApi";
import { normalizeNumber } from "../../utils/formattingFunctions";
import { FlashOffRounded } from "@mui/icons-material";

interface HolidayClubAttendanceChartProps {
  data: HolidayClubAttendanceNumberResponse[];
  title?: string;
  height?: number;
  chartType?: "bar" | "line";
}

export const HolidayClubAttendanceChart: React.FC<HolidayClubAttendanceChartProps> = ({
  data,
  title = "Rocketeers Attendance",
  height = 300,
  chartType = "bar",
}) => {
  // Memoize chart data for Recharts
  const chartData = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }

    // Sort data by booking date and format for chart
    return data
      .sort((a, b) => dayjs(a.bookingDate).valueOf() - dayjs(b.bookingDate).valueOf())
      .map((item) => ({
        date: dayjs(item.bookingDate).format("DD-MMM-YYYY"),
        fullDate: dayjs(item.bookingDate).format("YYYY-MM-DD"),
        value: item.value,
        serviceId: item.serviceId,
      }));
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" p={2}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Alert severity="info">No rocketeers attendance data available for this service</Alert>
      </Box>
    );
  }

  const ChartComponent = chartType === "line" ? LineChart : BarChart;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Box
          sx={{
            width: 4,
            height: 20,
            backgroundColor: "#5CE5BB",
            borderRadius: 1,
          }}
        />
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: "#2c3e50",
            fontFamily: "ABC Whyte",
          }}
        >
          {title}
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: "#6c757d",
          mb: 3,
          fontFamily: "ABC Whyte",
        }}
      >
        Rocketeers attendance numbers over time
      </Typography>
      <ResponsiveContainer width="100%" height={height}>
        <ChartComponent data={chartData} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="date"
            tickFormatter={(value) => dayjs(value).format("DD-MMM")}
            axisLine={false}
            tickLine={false}
            tick={{ fill: "#6c757d", fontSize: 16 }}
            angle={-45}
            textAnchor="end"
            height={80}
            tickMargin={30}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={false}
            label={{ value: "Attendance count", angle: -90, fill: "#6c757d", fontSize: 16 }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "#ffffff",
              border: "1px solid #e0e0e0",
              borderRadius: 8,
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            }}
            formatter={((value: number | undefined) => [value ?? 0, "Attendance"]) as any}
            labelFormatter={(label: React.ReactNode) => (label ? `Date: ${String(label)}` : "Date")}
          />
          {chartType === "line" ? (
            <Line
              type="monotone"
              dataKey="value"
              stroke="#5CE5BB"
              strokeWidth={3}
              dot={{ fill: "#5CE5BB", strokeWidth: 2, r: 6 }}
              activeDot={{ r: 8, stroke: "#5CE5BB", strokeWidth: 2 }}
            />
          ) : (
            <Bar dataKey="value" fill="#5CE5BB" radius={[32, 32, 0, 0]}>
              <LabelList
                dataKey="value"
                position="top"
                formatter={(value) => (value ? normalizeNumber(Number(value), 0) : "")}
                fontSize={18}
              />
            </Bar>
          )}
        </ChartComponent>
      </ResponsiveContainer>
    </Box>
  );
};
